/**
 * Community pharmacy qualification catalogue — England and Wales.
 *
 * IN ITS OWN FILE, ON PURPOSE. `gb.ts` already carries contract templates,
 * bank holidays, Bradford bands and two sector lists in ~700 lines; this
 * catalogue would roughly double it. It is also the part most certain to
 * churn: pharmacy technician supervision changes on 10 December 2026, and the
 * minimum qualification level rises to RQF Level 4 from autumn 2029. Each of
 * those should land as a small reviewable diff in one file rather than a hunk
 * buried in a general-purpose one.
 *
 * ── THREE AXES, NOT ONE ─────────────────────────────────────────────────────
 * The intuition that "pharmacy is devolved so everything needs a nation" is
 * wrong, and wrong in either direction is costly:
 *
 *   REGISTRATION is GB-wide. The GPhC registers pharmacists and technicians
 *     for England, Scotland and Wales together; somebody moving from Cardiff
 *     to Bristol does not re-register. Shared rows, both nations.
 *
 *   SERVICE DELIVERY is devolved. NHS England commissions Pharmacy First;
 *     Wales commissions its own services through HEIW. The training that
 *     accredits you for one does not accredit you for the other. Tagged rows.
 *
 *   REIMBURSEMENT is England-and-Wales-combined. Deliberately not modelled
 *     here — that is the Drug Tariff, and `service_fees` already carries it
 *     with a both-nations default.
 *
 * ── ONE ROW PER (QUALIFICATION, RULE-SET) ───────────────────────────────────
 * Where a rule is shared, ONE row carries both nations. Where it genuinely
 * diverges, TWO rows with DISTINCT NAMES — the seeder's idempotency check
 * matches on name and not on nations, so two rows sharing a name would
 * collide and one would silently win.
 *
 * ACPT is shared because the accreditation is explicitly portable across
 * England, Wales and Northern Ireland. Safeguarding L2 is split because the
 * Welsh accreditation expires on a 36-month clock and the English one does not
 * expire at all.
 *
 * ── `mandatory` IS DEAD DATA ────────────────────────────────────────────────
 * Every GB entry sets it and NOTHING reads it: `qualificationTypesSeed.ts`
 * never writes it and `CountryQualificationType` has no such column. It is set
 * here only to satisfy the shared `QualificationDefault` contract. Do not
 * build enforcement on it.
 *
 * What actually gates, from `gateService.ts`: the gate iterates
 * EmployeeQualification ROWS and blocks on any row with a past expiryDate. It
 * never consults the type. So seeding this catalogue gates NOTHING by itself —
 * a type only bites once an employee has a row against it. The statutory /
 * service distinction therefore rides on `category: 'service'`, which IS
 * persisted, plus QualificationRequirement rows.
 *
 * ── THE OPEN QUESTIONS WERE RESEARCHED, 2026-08-26 ──────────────────────────
 * All four carried over from Spec 1 have been chased to a published source and
 * the findings are recorded at the rows they affect. Summary:
 *
 *   Welsh DBS interval      RESOLVED. Wales publishes none, so nothing
 *     diverges. The three years is England's Pharmacy Quality Scheme, not law.
 *     A 2026/27 CPCF enhanced-DBS expectation lands ~October 2026 and will
 *     change this row's status.
 *   HEIW ACPT interval      RESOLVED for this row: ACPT is absent from HEIW's
 *     re-accreditation list, so non-expiring is evidenced. A separate
 *     population - non-registered accuracy checkers - does carry a 24-month
 *     reassessment and would need its own row.
 *   Welsh NMP interval      CONFIRMED annual (WHC/2024/02). The useful finding
 *     is the SCOPE: NHS settings, so a community contractor may not be bound.
 *   Common Ailments clock   CONFIRMED absent from HEIW's list. Omitting it was
 *     right.
 *
 * Nothing here was resolved by inference. Where a source is silent that is
 * recorded as silence, not filled in.
 *
 * Sources: pharmacyregulation.org (registration, support-staff route,
 * revalidation); cppe.ac.uk and NHS England community pharmacy service
 * specifications (English services and the Declaration of Competence cycle);
 * heiw.nhs.wales and wcppe.org.uk (Welsh reaccreditation clocks); Welsh
 * Government community pharmacy contractual framework.
 */
import type { JobTitleDefault, QualificationDefault } from './types';

const S = ['pharmacy'];

/*
 * ── serviceKeys: which billable service a `service` qualification unlocks ──
 * Added 2026-09-25 so a branch can see which fee-paying services its staff are
 * accredited for (GET /api/service-fees/accreditation-coverage). Set only where
 * the qualification on its own is what the service requires, and only to keys
 * that exist in the fee schedule for the same nation.
 *
 * Deliberately left unmapped:
 *   - Clinical Assessment & Examination (otoscopy), BLS & Anaphylaxis — they
 *     support Pharmacy First and vaccination; neither is sufficient alone, so
 *     mapping them would count a holder as able to deliver the service.
 *   - RSPH L2 Understanding Health Improvement — Healthy Living Pharmacy; no fee.
 *   - Needle & Syringe L2/L3, Problematic Substances Use, Stop Smoking (Wales)
 *     — no Welsh fee line for these services in the schedule.
 *   - Respiratory Medicines Adherence (Wales) — the schedule's Inhaler Review
 *     Service may be what it accredits, but nothing here establishes that.
 *   - PIPS — `pips_experiential` pays for experiential learning and clinical
 *     links, not for delivering the service this accredits.
 */

export const pharmacyQualificationDefaults: QualificationDefault[] = [
  /* ── Statutory and professional — 13 rows ─────────────────────────────────
   * Eight shared, five nation-specific, so an England tenant provisions 10 of
   * these and a Wales tenant 11. */
  {
    name: 'GPhC Registration',
    category: 'regulatory',
    issuingBody: 'General Pharmaceutical Council',
    validityMonths: 12,
    enforcementLevel: 1,
    gracePeriodDays: 0,
    mandatory: true,
    sectors: S,
    reminderDays: [90, 60, 30, 14, 7],
  },
  {
    /* SYNTHETIC, and the one row that uses the start-relative path.
     *
     * The GPhC rule is a completion DEADLINE, not a recurring expiry: enrol
     * within three months of starting, normally complete within three years,
     * supervised by a pharmacist, pharmacy technician or accountable GP. So
     * onboarding writes one row per support-staff hire with
     * expiryDate = startDate + 36 months, the gate counts it down, and once
     * achieved it never recurs. Same pattern as Right to Work with a visa. */
    name: 'Pharmacy Support Staff Training (GPhC)',
    category: 'regulatory',
    issuingBody: 'General Pharmaceutical Council',
    validityMonths: null,
    enforcementLevel: 1,
    gracePeriodDays: 0,
    mandatory: true,
    sectors: S,
    reminderDays: [180, 90, 30, 14],
    dueFromStartMonths: 36,
  },
  {
    /*
     * RESEARCHED 2026-08-26, and the answer changes what this row means.
     *
     * WALES SETS NO DISTINCT PHARMACY INTERVAL. Community Pharmacy Wales
     * publishes DBS guidance and forms but states no renewal frequency, so
     * there is nothing to diverge from. One shared row is correct - not as a
     * placeholder, but because the question has been asked and answered.
     *
     * THE THREE YEARS IS ENGLISH AND CONTRACTUAL, NOT STATUTORY. A DBS
     * certificate carries no expiry date at all. The clock comes from the
     * Pharmacy Quality Scheme 2025/26, which required a certificate "issued
     * within the last three years", explicitly to bring pharmacy into line
     * with other NHS healthcare professionals. Treat 36 months as a scheme
     * requirement a contractor is measured against, not a law.
     *
     * WATCH THIS ONE. Under the 2026/27 CPCF settlement all pharmacists and
     * pharmacy technicians are expected to hold an ENHANCED DBS check, with
     * implementation expected October 2026 - weeks away. When that lands,
     * `mandatory` becomes true in substance for those two roles and this row
     * will want a requirement mapping rather than being advisory.
     *
     * Sources: cpe.org.uk DBS checks guidance (PQS 2025/26 three-year cycle,
     * 2026/27 CPCF enhanced-DBS expectation); cpwales.org.uk DBS checks.
     */
    name: 'Enhanced DBS Check',
    category: 'regulatory',
    issuingBody: 'Disclosure and Barring Service',
    validityMonths: 36,
    enforcementLevel: 1,
    gracePeriodDays: 30,
    mandatory: true,
    sectors: S,
    reminderDays: [90, 60, 30],
  },
  {
    name: 'Information Governance / Data Security',
    category: 'regulatory',
    issuingBody: 'NHS / employer policy',
    validityMonths: 12,
    enforcementLevel: 2,
    gracePeriodDays: 30,
    mandatory: true,
    sectors: S,
    reminderDays: [60, 30],
  },
  {
    /* SPLIT FROM WALES. The English employer duty does not expire; the Welsh
     * HEIW accreditation runs a 36-month clock. Distinct names because the
     * seeder matches on name. */
    name: 'Safeguarding Level 2',
    category: 'regulatory',
    issuingBody: 'Intercollegiate guidance',
    validityMonths: null,
    enforcementLevel: 2,
    gracePeriodDays: 30,
    mandatory: true,
    sectors: S,
    reminderDays: [],
    nations: ['ENG'],
  },
  {
    name: 'Safeguarding Children & Young People L2 (HEIW)',
    category: 'regulatory',
    issuingBody: 'HEIW',
    validityMonths: 36,
    enforcementLevel: 2,
    gracePeriodDays: 30,
    mandatory: true,
    sectors: S,
    reminderDays: [90, 60, 30],
    nations: ['WLS'],
  },
  {
    /* THE ROW THAT DEMONSTRATES THE WHOLE DESIGN. In Wales this is a mandatory
     * annual face-to-face HEIW accreditation underpinning every clinical
     * service, so it sits here as statutory. In England it is three-yearly and
     * required only BECAUSE the pharmacy delivers the NHS flu service, so it
     * appears in the England service block as advisory. One skill, two rows,
     * opposite compliance treatment, expressed as data rather than prose. */
    name: 'Basic Life Support (HEIW)',
    category: 'safety',
    issuingBody: 'HEIW',
    validityMonths: 12,
    enforcementLevel: 1,
    gracePeriodDays: 14,
    mandatory: true,
    sectors: S,
    reminderDays: [60, 30, 14],
    nations: ['WLS'],
  },
  {
    name: 'BTEC L2 Certificate, Pharmacy Support Staff',
    category: 'professional',
    issuingBody: 'Accredited provider',
    validityMonths: null,
    enforcementLevel: 2,
    gracePeriodDays: 0,
    mandatory: false,
    sectors: S,
    reminderDays: [],
  },
  {
    name: 'L3 Diploma, Pharmacy Technician',
    category: 'professional',
    issuingBody: 'Accredited provider',
    validityMonths: null,
    enforcementLevel: 3,
    gracePeriodDays: 0,
    mandatory: false,
    sectors: S,
    reminderDays: [],
  },
  {
    /*
     * SHARED, not split: the accreditation is explicitly portable across
     * England, Wales and Northern Ireland.
     *
     * RESEARCHED 2026-08-26 and left non-expiring, now on evidence rather
     * than absence of it: ACPT does NOT appear in HEIW's published
     * re-accreditation list, which names thirteen services and their clocks
     * and does not include it. So Wales sets no interval for it.
     *
     * PARTIALLY OPEN, and the distinction matters. Training providers state a
     * two-yearly reassessment for accuracy checkers who are NOT GPhC-
     * registered pharmacy technicians, and that a gap over 24 months without
     * checking requires the full qualification again. That is a different
     * population from this row, which is the technician route. If the product
     * ever models non-registered checkers, they need their own row with a
     * 24-month clock - not this one widened.
     *
     * Source: cpwales.org.uk HEIW re-accreditation list (ACPT absent).
     */
    name: 'Accuracy Checking Pharmacy Technician',
    category: 'professional',
    issuingBody: 'Accredited provider',
    validityMonths: null,
    enforcementLevel: 2,
    gracePeriodDays: 0,
    mandatory: false,
    sectors: S,
    reminderDays: [],
  },
  {
    /* An annotation on the GPhC register, not a separate registration, and it
     * does not lapse on its own — revalidation carries it. */
    name: 'Independent Prescriber annotation',
    category: 'professional',
    issuingBody: 'General Pharmaceutical Council',
    validityMonths: null,
    enforcementLevel: 2,
    gracePeriodDays: 0,
    mandatory: false,
    sectors: S,
    reminderDays: [],
  },
  {
    /* CONDITIONAL: seeded not-mandatory with a role requirement that applies
     * only to prescribers. Required of a pharmacist holding the IP annotation
     * and of nobody else. Not a third gate class — the statutory / service
     * split still holds. */
    name: 'Prescribing Scope of Practice Declaration',
    category: 'regulatory',
    issuingBody: 'NHS England',
    validityMonths: 12,
    enforcementLevel: 1,
    gracePeriodDays: 0,
    mandatory: false,
    sectors: S,
    reminderDays: [90, 60, 30],
    nations: ['ENG'],
  },
  {
    /*
     * Conditional, as above.
     *
     * INTERVAL CONFIRMED 2026-08-26: annual. The Welsh Government's Standards
     * for Competency Assurance of Non-medical Prescribers (WHC/2024/02)
     * require an annual self-assessment of prescribing competence and an
     * annual declaration of scope of practice and continued competence. The
     * provisional 12 months was right.
     *
     * BUT THE SCOPE IS NARROWER THAN THIS ROW IMPLIES, which is the more
     * useful finding. The standards apply to prescribers "who provide NHS
     * services to patients in Wales" and are directed at health boards and
     * trusts. A community pharmacy contractor outside NHS employment is not
     * plainly in scope. The row is therefore correctly `mandatory: false`
     * with a prescriber-only requirement, and should NOT be promoted to a
     * blanket obligation on the strength of the interval being confirmed.
     *
     * The accompanying prescribing appraisal is required "regular" with no
     * stated frequency, so it is not modelled as a separate row.
     *
     * Source: gov.wales WHC/2024/02.
     */
    name: 'NMP Competency Assurance (Wales)',
    category: 'regulatory',
    issuingBody: 'HEIW',
    validityMonths: 12,
    enforcementLevel: 1,
    gracePeriodDays: 0,
    mandatory: false,
    sectors: S,
    reminderDays: [90, 60, 30],
    nations: ['WLS'],
  },

  /* ── England service — 9 rows ─────────────────────────────────────────────
   * All `service`, all ENG. Most carry 24 months to match the Declaration of
   * Competence cycle, which NHS England supports nationally and which is
   * normally completed at least every two years where a service specifies it. */
  {
    name: 'Pharmacy First pathways & PGDs',
    category: 'service',
    issuingBody: 'CPPE / NHS England',
    validityMonths: 24,
    enforcementLevel: 2,
    gracePeriodDays: 0,
    mandatory: false,
    sectors: S,
    reminderDays: [90, 30],
    nations: ['ENG'],
    serviceKeys: ['pharmacy_first'],
  },
  {
    name: 'Clinical Assessment & Examination (otoscopy)',
    category: 'service',
    issuingBody: 'CPPE / NHS England',
    validityMonths: 24,
    enforcementLevel: 2,
    gracePeriodDays: 0,
    mandatory: false,
    sectors: S,
    reminderDays: [90, 30],
    nations: ['ENG'],
  },
  {
    name: 'Vaccination Competence',
    category: 'service',
    issuingBody: 'CPPE / NHS England',
    validityMonths: 24,
    enforcementLevel: 2,
    gracePeriodDays: 0,
    mandatory: false,
    sectors: S,
    reminderDays: [90, 30],
    nations: ['ENG'],
    serviceKeys: ['flu_vaccination', 'covid_vaccination'],
  },
  {
    /* The English half of the Basic Life Support pair. Three-yearly, and
     * advisory because it is required by the flu service rather than in its
     * own right. Its Welsh counterpart is statutory and annual, above. */
    name: 'BLS & Anaphylaxis',
    category: 'service',
    issuingBody: 'CPPE / NHS England',
    validityMonths: 36,
    enforcementLevel: 2,
    gracePeriodDays: 14,
    mandatory: false,
    sectors: S,
    reminderDays: [90, 30],
    nations: ['ENG'],
  },
  {
    name: 'Hypertension Case-Finding',
    category: 'service',
    issuingBody: 'CPPE / NHS England',
    validityMonths: 24,
    enforcementLevel: 3,
    gracePeriodDays: 0,
    mandatory: false,
    sectors: S,
    reminderDays: [60, 30],
    nations: ['ENG'],
    serviceKeys: ['hypertension'],
  },
  {
    name: 'Pharmacy Contraception Service',
    category: 'service',
    issuingBody: 'CPPE / NHS England',
    validityMonths: 24,
    enforcementLevel: 3,
    gracePeriodDays: 0,
    mandatory: false,
    sectors: S,
    reminderDays: [60, 30],
    nations: ['ENG'],
    serviceKeys: ['contraception'],
  },
  {
    name: 'New Medicine Service',
    category: 'service',
    issuingBody: 'CPPE / NHS England',
    validityMonths: 24,
    enforcementLevel: 3,
    gracePeriodDays: 0,
    mandatory: false,
    sectors: S,
    reminderDays: [60, 30],
    nations: ['ENG'],
    serviceKeys: ['nms'],
  },
  {
    name: 'NCSCT Stop Smoking Practitioner',
    category: 'service',
    issuingBody: 'NCSCT',
    validityMonths: 24,
    enforcementLevel: 3,
    gracePeriodDays: 30,
    mandatory: false,
    sectors: S,
    reminderDays: [60, 30],
    nations: ['ENG'],
    serviceKeys: ['smoking_cessation'],
  },
  {
    name: 'RSPH L2 Understanding Health Improvement',
    category: 'service',
    issuingBody: 'RSPH',
    validityMonths: null,
    enforcementLevel: 3,
    gracePeriodDays: 0,
    mandatory: false,
    sectors: S,
    reminderDays: [],
    nations: ['ENG'],
  },

  /* ── Wales service — 12 rows ──────────────────────────────────────────────
   * All `service`, all WLS, carrying HEIW's real reaccreditation clocks rather
   * than an inferred cycle.
   *
   * Basic Life Support and Safeguarding L2 are NOT here — in Wales they are
   * mandatory rather than service-conditional, so they sit in the statutory
   * block above.
   *
   * The Common Ailments Service is deliberately ABSENT, and this is now
   * CONFIRMED rather than assumed (researched 2026-08-26): HEIW's published
   * re-accreditation list names thirteen items and the Common Ailments Service
   * is not among them. It is bundled into the Clinical Community Pharmacy
   * Service but carries no published reaccreditation clock, so omitting it was
   * right - an invented interval would have expired a competence nobody
   * requires to be renewed.
   *
   * THE SAME SOURCE CONFIRMS EVERY CLOCK BELOW. Basic Life Support and
   * Seasonal Influenza are annual; the remaining eleven are three-yearly,
   * exactly as seeded. Safeguarding Children & Young People L2 also appears
   * there at three-yearly, which is why it sits in the statutory block above
   * with a 36-month clock rather than here.
   *
   * Source: cpwales.org.uk HEIW re-accreditation for services. */
  {
    name: 'Seasonal Influenza Vaccination (Wales)',
    category: 'service',
    issuingBody: 'HEIW',
    validityMonths: 12,
    enforcementLevel: 1,
    gracePeriodDays: 0,
    mandatory: false,
    sectors: S,
    reminderDays: [90, 60, 30],
    nations: ['WLS'],
    serviceKeys: ['flu_wales'],
  },
  {
    name: 'Blood Borne Viruses (Wales)',
    category: 'service',
    issuingBody: 'HEIW',
    validityMonths: 36,
    enforcementLevel: 3,
    gracePeriodDays: 0,
    mandatory: false,
    sectors: S,
    reminderDays: [90, 30],
    nations: ['WLS'],
    serviceKeys: ['bbv_screening'],
  },
  {
    name: 'Contraception Services (Wales)',
    category: 'service',
    issuingBody: 'HEIW',
    validityMonths: 36,
    enforcementLevel: 3,
    gracePeriodDays: 0,
    mandatory: false,
    sectors: S,
    reminderDays: [90, 30],
    nations: ['WLS'],
    serviceKeys: ['contraception_wales'],
  },
  {
    name: 'Discharge Medicine Review (DMR)',
    category: 'service',
    issuingBody: 'HEIW',
    validityMonths: 36,
    enforcementLevel: 2,
    gracePeriodDays: 0,
    mandatory: false,
    sectors: S,
    reminderDays: [90, 30],
    nations: ['WLS'],
    serviceKeys: ['welsh_dmr'],
  },
  {
    name: 'Needle & Syringe Level 2 (Wales)',
    category: 'service',
    issuingBody: 'HEIW',
    validityMonths: 36,
    enforcementLevel: 3,
    gracePeriodDays: 0,
    mandatory: false,
    sectors: S,
    reminderDays: [90, 30],
    nations: ['WLS'],
  },
  {
    name: 'Needle & Syringe Level 3 (Naloxone, Wales)',
    category: 'service',
    issuingBody: 'HEIW',
    validityMonths: 36,
    enforcementLevel: 3,
    gracePeriodDays: 0,
    mandatory: false,
    sectors: S,
    reminderDays: [90, 30],
    nations: ['WLS'],
  },
  {
    name: 'Problematic Substances Use (Wales)',
    category: 'service',
    issuingBody: 'HEIW',
    validityMonths: 36,
    enforcementLevel: 3,
    gracePeriodDays: 0,
    mandatory: false,
    sectors: S,
    reminderDays: [90, 30],
    nations: ['WLS'],
  },
  {
    name: 'Respiratory Medicines Adherence (Wales)',
    category: 'service',
    issuingBody: 'HEIW',
    validityMonths: 36,
    enforcementLevel: 3,
    gracePeriodDays: 0,
    mandatory: false,
    sectors: S,
    reminderDays: [90, 30],
    nations: ['WLS'],
  },
  {
    name: 'Sore Throat Test and Treat (STTT)',
    category: 'service',
    issuingBody: 'HEIW',
    validityMonths: 36,
    enforcementLevel: 2,
    gracePeriodDays: 0,
    mandatory: false,
    sectors: S,
    reminderDays: [90, 30],
    nations: ['WLS'],
    serviceKeys: ['cas_sore_throat'],
  },
  {
    name: 'Stop Smoking (Wales)',
    category: 'service',
    issuingBody: 'HEIW',
    validityMonths: 36,
    enforcementLevel: 3,
    gracePeriodDays: 0,
    mandatory: false,
    sectors: S,
    reminderDays: [90, 30],
    nations: ['WLS'],
  },
  {
    name: 'Urinary Tract Infection (UTI, Wales)',
    category: 'service',
    issuingBody: 'HEIW',
    validityMonths: 36,
    enforcementLevel: 2,
    gracePeriodDays: 0,
    mandatory: false,
    sectors: S,
    reminderDays: [90, 30],
    nations: ['WLS'],
    serviceKeys: ['cas_uti'],
  },
  {
    /* No published reaccreditation interval — non-expiring rather than given
     * an invented clock. */
    name: 'Pharmacist Independent Prescribing Service (PIPS)',
    category: 'service',
    issuingBody: 'HEIW',
    validityMonths: null,
    enforcementLevel: 2,
    gracePeriodDays: 0,
    mandatory: false,
    sectors: S,
    reminderDays: [],
    nations: ['WLS'],
  },
];

/**
 * The job titles a pharmacy company starts with, and what each covers on the
 * rota. Written to the company's own list the first time it is used; from then
 * on the company edits its list and this is not read again.
 *
 * RP is implied by being a pharmacist: the RP log signs a pharmacist in, and the
 * GPhC registration check decides whether they may.
 */
export const pharmacyJobTitleDefaults: JobTitleDefault[] = [
  { name: 'Pharmacist', rotaRole: 'pharmacist' },
  { name: 'Locum Pharmacist', rotaRole: 'pharmacist' },
  { name: 'Superintendent Pharmacist', rotaRole: 'pharmacist' },
  { name: 'Independent Prescriber Pharmacist', rotaRole: 'pharmacist_ip', aliases: ['Independent Prescriber'] },
  { name: 'Accuracy Checking Technician (ACT)', rotaRole: 'act', aliases: ['Accuracy Checking Technician', 'ACT'] },
  { name: 'Pharmacy Technician', rotaRole: 'technician' },
  { name: 'Dispenser', rotaRole: 'dispenser' },
  { name: 'Pharmacy Assistant', rotaRole: 'counter_assistant' },
  { name: 'Counter Assistant', rotaRole: 'counter_assistant' },
  { name: 'Delivery Driver', rotaRole: 'driver', aliases: ['Driver'] },
];
